// error-boundary.tsx — one boundary, wrapped around every island in mount.tsx.
//
// Islands read server payloads (data-props, fetch results) that are typed but
// not runtime-validated, so a shape change or a bad blob can throw during
// render. Without a boundary a throw blanks the island's subtree with only a
// console error (and prod has no sourcemaps). This catches the throw and shows
// a calm, house-style fallback the user can recover from, scoped to the one
// island that failed. The rest of the page keeps working.
import { Component, type ErrorInfo, type ReactNode } from 'react'

interface Props {
  /** Island name, for the console log and so support can identify the panel. */
  name: string
  children: ReactNode
}

interface State {
  error: Error | null
}

export class IslandErrorBoundary extends Component<Props, State> {
  state: State = { error: null }

  static getDerivedStateFromError(error: Error): State {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface enough to debug without a sourcemap. The island name pins the
    // failure to a panel; the component stack points at the throwing tree.
    console.error(`[islands] "${this.props.name}" crashed`, error, info.componentStack)
  }

  render(): ReactNode {
    if (this.state.error) {
      return (
        <div role="alert" className="island-error">
          <p>This panel could not load. Reload the page to try again.</p>
        </div>
      )
    }
    return this.props.children
  }
}
